/**
 * Assistant Module API (Phase 29)
 *
 * WHY: Provides a mission-aware AI chat surface with tool calling and auditable persistence.
 * All requests route through the Platform gateway (`/api/v1`) per architecture mandates.
 */

import { http } from '../core/client'
import type {
  AssistantAgentsResponse,
  AssistantModelsResponse,
  AssistantSession,
  AssistantMessagesResponse,
  AssistantAttachment,
  AssistantAction,
} from '../core/types'

const buildQueryString = (params?: Record<string, string | number | boolean | undefined>) => {
  if (!params) return ''
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return
    search.set(key, String(value))
  })
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const assistantApi = {
  agents: {
    list: () => http.get<AssistantAgentsResponse>('/assistant/agents', { skipMissionContext: true }),
  },
  models: {
    list: () => http.get<AssistantModelsResponse>('/assistant/models', { skipMissionContext: true }),
  },
  sessions: {
    create: (data: {
      mission_id: string
      agent_id?: string | null
      provider_integration_id: string
      model_id: string
      title?: string | null
    }) => http.post<AssistantSession>('/assistant/sessions', data),
    list: (params?: { mission_id?: string }) =>
      http.get<AssistantSession[]>(`/assistant/sessions${buildQueryString(params)}`),
    messages: (sessionId: string) =>
      http.get<AssistantMessagesResponse>(`/assistant/sessions/${sessionId}/messages`),
    approve: (actionId: string) =>
      http.post<AssistantAction>(`/assistant/actions/${actionId}/approve`, {}),
    reject: (actionId: string) =>
      http.post<AssistantAction>(`/assistant/actions/${actionId}/reject`, {}),
    uploadAttachment: async (sessionId: string, file: File): Promise<AssistantAttachment> => {
      const formData = new FormData()
      formData.append('file', file)
      return http.post<AssistantAttachment>(`/assistant/sessions/${sessionId}/attachments`, formData as unknown)
    },
  },
} as const

